import { Component, EventEmitter, Output, HostListener, ElementRef, Input, OnChanges, OnInit, SimpleChanges, inject, input, computed } from '@angular/core';
import { CosmeticStyleClipboardService } from '../../../services/cosmetic-style-clipboard.service';
import { StyleLibraryPickerDialogComponent } from '../style-library-picker-dialog/style-library-picker-dialog.component';
import { PreviewCustomRule, DisplayOptions, Displayable } from '../../../models/filter-models';
import { DisplayType } from "../../../models/enums";
import { Rarity } from "../../../models/enums";
import { CosmeticOptions } from '../../../models/filter-models';
import { RuleId } from '../../../models/rule-id';
import { FilterService } from '../../../services/filter.service';
import { FilterStoreService } from '../../../services/filter-store.service';
import { EquipmentTierService } from '../../../services/tiering/equipment-tier.service';
import { RulePreviewComponent } from '../../rule-preview/rule-preview.component';
import { CosmeticSettingsComponent } from '../../../cosmetic-tab/cosmetic-settings/cosmetic-settings.component';
import { CosmeticControls } from '../../../cosmetic-tab/cosmetic-controls.model';
import { TierSelectorComponent } from '../../select/select-tier/select-tier.component';
import { CosmeticsGeneratorService } from '../../../services/cosmetics-generator.service';
import { Tier } from '../../../models/item-data-models';

@Component({
  selector: 'app-style-picker-dialog',
  imports: [RulePreviewComponent, CosmeticSettingsComponent, TierSelectorComponent, StyleLibraryPickerDialogComponent],
  templateUrl: './style-picker-dialog.component.html',
  styleUrls: ['../../../common/dialogs.scss', './style-picker-dialog.component.scss']
})
export class StylePickerDialogComponent implements OnInit, OnChanges {
  private readonly filterStoreService = inject(FilterStoreService);
  private readonly equipmentTierService = inject(EquipmentTierService);
  private readonly styleClipboard = inject(CosmeticStyleClipboardService);

  @Output() close = new EventEmitter<void>();
  @Output() styleUpdated = new EventEmitter<void>();
  @Output() customRuleStyleCleared = new EventEmitter<void>();

  @Input({ required: true }) displayable!: Displayable;
  ruleId = input<RuleId>();
  @Input() defaultCosmeticOptions?: CosmeticOptions;
  @Input() exampleName?: string;
  @Input() rarity?: Rarity;
  @Input() sockets?: string;
  @Input() influence?: string;
  @Input() fractured?: boolean;
  @Input() veiled?: boolean;
  @Input() isCurrency?: boolean;
  @Input() isWaystone?: boolean;
  @Input() isPoe1Gem?: boolean;
  @Input() isQuestItem?: boolean;
  title = input<string>();

  @Input() allowDisactivate = true;
  @Input() allowHide = true;

  Tier = Tier;
  Rarity = Rarity;
  DisplayType = DisplayType;

  previewRule = new PreviewCustomRule({ displayOptions: new DisplayOptions({ cosmeticOptions: new CosmeticOptions() }) });
  cosmeticControls = new CosmeticControls({ labelOptionsOpen: true, highlightOptionsOpen: true, soundOptionsOpen: true });

  canCopyStyle = false;
  hasClipboard = this.styleClipboard.hasClipboard;
  hasLibraryEntries = this.styleClipboard.hasLibraryEntries;
  libraryPickerOpen = false;

  dialogTitle = computed(() => {
    const title = this.title();
    if (title) return title;
    const ruleId = this.ruleId();
    if (!ruleId) return undefined;
    return this.equipmentTierService.getMetadataForRuleId(ruleId)?.displayName;
  });

  canClearUserCustomRuleStyle = computed(() => {
    const ruleId = this.ruleId();
    if (!ruleId) return false;
    return this.filterStoreService.activeFilter().customRuleStyles.some(s => s.ruleId === ruleId && s.custom);
  });

  constructor(private elementRef: ElementRef, public filterService: FilterService, private cosmeticsGeneratorService: CosmeticsGeneratorService) {
  }

  ngOnInit(): void {
    this.refreshClipboardButtonState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['displayable']) {
      Object.assign(this.previewRule.displayOptions, { ...this.displayable.displayOptions, cosmeticOptions: { ...this.displayable.displayOptions.cosmeticOptions } });
      this.setCosmeticOptionsFromDisplayOptions();
    }

    if (changes['exampleName']) {
      this.previewRule.exampleName = this.exampleName;
      this.refreshClipboardButtonState();
    }

    if (changes['rarity']) {
      this.previewRule.rarity = this.rarity || Rarity.Normal;
    }

    if (changes['isPoe1Gem']) {
      this.previewRule.isPoe1Gem = this.isPoe1Gem;
    }

    if (changes['isQuestItem']) {
      this.previewRule.isQuestItem = this.isQuestItem;
    }

    if (changes['influence']) {
      this.previewRule.influence = this.influence;
    }

    if (changes['fractured']) {
      this.previewRule.fractured = this.fractured;
    }

    if (changes['veiled']) {
      this.previewRule.veiled = this.veiled;
    }

    if (changes['sockets']) {
      this.previewRule.sockets = this.sockets;
    }

    if (changes['isCurrency']) {
      this.previewRule.isCurrency = this.isCurrency;
    }
  }

  private refreshClipboardButtonState() {
    const options = this.styleClipboard.resolveOptionsForCopy(
      this.previewRule.displayOptions,
      this.exampleName,
      this.dialogTitle(),
    );
    this.canCopyStyle = this.styleClipboard.canCopy(options);
  }


  tierChanged() {
    if (this.previewRule.displayOptions.displayTypeTier === Tier.FHidden) {
      this.previewRule.displayOptions.hidden = true;
    } else {
      this.previewRule.displayOptions.hidden = false;
    }
    this.setCosmeticOptionsFromDisplayOptions();
  }

  setCosmeticOptionsFromDisplayOptions() {
    const result = this.cosmeticsGeneratorService.getCosmeticOptionsFromDisplayOptions(this.previewRule.displayOptions, this.exampleName, this.title());

    this.previewRule.displayOptions = new DisplayOptions({ ...this.previewRule.displayOptions, cosmeticOptions: result.cosmeticOptions, hidden: result.hidden });
    this.refreshClipboardButtonState();
  }

  switchToDisplayType(displayType: DisplayType) {
    this.previewRule.displayOptions.displayType = displayType;
    if (displayType === DisplayType.Custom && !this.previewRule.displayOptions.cosmeticOptions) {
      this.previewRule.displayOptions.cosmeticOptions = new CosmeticOptions();
    } else if ((displayType === DisplayType.Tier || displayType === DisplayType.DivCardTier || displayType === DisplayType.EquipmentTier) && !this.previewRule.displayOptions.displayTypeTier) {
      this.previewRule.displayOptions.displayTypeTier = Tier.DWhite;
    }
    this.setCosmeticOptionsFromDisplayOptions();
  }

  toggleHidden() {
    this.previewRule.displayOptions.hidden = !this.previewRule.displayOptions.hidden;
  }

  applyStyle() {
    this.displayable.displayOptions = new DisplayOptions(this.previewRule.displayOptions);

    this.styleUpdated.emit();
    this.filterService.updateFilter();
    this.closeDialog();
  }

  resetDefaultStyle() {
    if (this.displayable.displayOptions.cosmeticOptions) {
      Object.assign(this.displayable.displayOptions.cosmeticOptions, this.defaultCosmeticOptions);
    }
    this.filterService.updateFilter();
    this.closeDialog();
  }

  clearUserCustomRuleStyle() {
    const ruleId = this.ruleId();
    if (!ruleId) return;
    const filter = this.filterStoreService.activeFilter();
    const idx = filter.customRuleStyles.findIndex(s => s.ruleId === ruleId && s.custom);
    if (idx < 0) return;
    filter.customRuleStyles.splice(idx, 1);
    this.filterService.updateFilter();
    this.customRuleStyleCleared.emit();
    this.closeDialog();
  }

  // True when the current pointer interaction started outside dialog content.
  // Prevents closing when dragging the color picker and releasing on the backdrop.
  private pointerDownOutsideDialog = false;

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    this.pointerDownOutsideDialog = !this.isInsideDialogContent(event.target as Node);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.pointerDownOutsideDialog) return;
    if (!this.isInsideDialogContent(event.target as Node)) {
      this.closeDialog();
    }
  }

  private isInsideDialogContent(target: Node): boolean {
    const host = this.elementRef.nativeElement;
    const dialogElement = host.querySelector('.dialog');
    if (dialogElement?.contains(target)) return true;

    const libraryPicker = host.querySelector('app-style-library-picker-dialog');
    if (libraryPicker?.contains(target)) return true;

    // ngx-color-picker may mount outside the dialog host
    const el = target instanceof Element ? target : target.parentElement;
    return !!el?.closest('.color-picker');
  }

  closeDialog() {
    this.close.emit();
  }

  copyStyle() {
    if (!this.canCopyStyle) return;
    const options = this.styleClipboard.resolveOptionsForCopy(
      this.previewRule.displayOptions,
      this.exampleName,
      this.dialogTitle(),
    );
    this.styleClipboard.copy(options, this.dialogTitle());
    this.refreshClipboardButtonState();
  }

  pasteStyleToPreview(options: CosmeticOptions) {
    if (this.previewRule.displayOptions.displayType !== DisplayType.Custom) {
      this.switchToDisplayType(DisplayType.Custom);
    }
    if (!this.previewRule.displayOptions.cosmeticOptions) {
      this.previewRule.displayOptions.cosmeticOptions = new CosmeticOptions();
    }
    this.styleClipboard.pasteTo(this.previewRule.displayOptions.cosmeticOptions, options);
  }

  pasteStyle() {
    const options = this.styleClipboard.getClipboardOptions();
    if (!options) return;
    this.pasteStyleToPreview(options);
  }

  openLibraryPicker() {
    if (!this.hasLibraryEntries()) return;
    this.libraryPickerOpen = true;
  }

  closeLibraryPicker() {
    this.libraryPickerOpen = false;
  }

  onLibraryStyleSelected(options: CosmeticOptions) {
    this.pasteStyleToPreview(options);
    this.closeLibraryPicker();
    this.refreshClipboardButtonState();
  }
}
